import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import { PencilSimple, Trash, Plus, PushPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const CABIN_OPTIONS = [
  { value: "economy", label: "Economy" },
  { value: "premium_economy", label: "Premium Economy" },
  { value: "business", label: "Business" },
];

const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: new Date(0, i).toLocaleString('en', { month: 'long' }) }));

export default function ManualOverride() {
  const { api, user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    route_id: "", airline_iata: "", travel_month: "", travel_year: new Date().getFullYear().toString(),
    cabin_class: "economy", fare_nzd: "", source_note: "", valid_until: "",
  });

  const canEdit = user?.role === "admin" || user?.role === "analyst";

  useEffect(() => {
    Promise.all([api.get("/routes", { params: { programme: "all" } }), api.get("/airlines"), api.get("/overrides")])
      .then(([r, a, o]) => { setRoutes(r.data); setAirlines(a.data); setOverrides(o.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, travel_month: parseInt(form.travel_month), travel_year: parseInt(form.travel_year), fare_nzd: parseFloat(form.fare_nzd) };
      const res = await api.post("/overrides", payload);
      setOverrides(prev => [res.data, ...prev]);
      setShowForm(false);
      setForm({ route_id: "", airline_iata: "", travel_month: "", travel_year: new Date().getFullYear().toString(), cabin_class: "economy", fare_nzd: "", source_note: "", valid_until: "" });
      toast.success("Override added successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add override");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this override?")) return;
    await api.delete(`/overrides/${id}`);
    setOverrides(prev => prev.filter(o => o.id !== id));
    toast.success("Override deleted");
  };

  const routeMap = Object.fromEntries(routes.map(r => [r.id, `AKL-${r.destination_code}`]));

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>;

  return (
    <div data-testid="overrides-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-syne font-bold text-2xl text-white tracking-tight">Manual Fare Overrides</h1>
          <p className="text-text-secondary text-sm mt-1">Override auto-fetched fares with confirmed data</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowForm(!showForm)} data-testid="add-override-btn"
            className="bg-ci-orange hover:bg-ci-orange/90 text-white h-9 px-4 text-sm">
            <Plus size={16} className="mr-1" /> Add Override
          </Button>
        )}
      </div>

      {/* Add Override Form */}
      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-card-bg border border-ci-orange/30 rounded-xl p-6">
          <h3 className="font-syne font-bold text-white mb-4">New Fare Override</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-text-secondary text-xs mb-1 block">Route</Label>
              <Select value={form.route_id} onValueChange={(v) => setForm(f => ({ ...f, route_id: v }))}>
                <SelectTrigger data-testid="override-route-select" className="bg-navy border-[rgba(255,255,255,0.1)] text-white h-10"><SelectValue placeholder="Select route" /></SelectTrigger>
                <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                  {routes.map(r => <SelectItem key={r.id} value={r.id}>AKL - {r.destination_code} ({r.destination_city})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-text-secondary text-xs mb-1 block">Airline</Label>
              <Select value={form.airline_iata} onValueChange={(v) => setForm(f => ({ ...f, airline_iata: v }))}>
                <SelectTrigger data-testid="override-airline-select" className="bg-navy border-[rgba(255,255,255,0.1)] text-white h-10"><SelectValue placeholder="Select airline" /></SelectTrigger>
                <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                  {airlines.map(a => <SelectItem key={a.iata_code} value={a.iata_code}>{a.iata_code} - {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-text-secondary text-xs mb-1 block">Cabin Class</Label>
              <Select value={form.cabin_class} onValueChange={(v) => setForm(f => ({ ...f, cabin_class: v }))}>
                <SelectTrigger data-testid="override-cabin-select" className="bg-navy border-[rgba(255,255,255,0.1)] text-white h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                  {CABIN_OPTIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-text-secondary text-xs mb-1 block">Travel Month</Label>
              <Select value={form.travel_month} onValueChange={(v) => setForm(f => ({ ...f, travel_month: v }))}>
                <SelectTrigger data-testid="override-month-select" className="bg-navy border-[rgba(255,255,255,0.1)] text-white h-10"><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent className="bg-card-bg border-[rgba(255,255,255,0.1)]">
                  {MONTHS.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-text-secondary text-xs mb-1 block">Travel Year</Label>
              <Input data-testid="override-year-input" type="number" value={form.travel_year} onChange={e => setForm(f => ({ ...f, travel_year: e.target.value }))}
                className="bg-navy border-[rgba(255,255,255,0.1)] text-white h-10" />
            </div>
            <div>
              <Label className="text-text-secondary text-xs mb-1 block">Fare (NZD)</Label>
              <Input data-testid="override-fare-input" type="number" step="0.01" value={form.fare_nzd} onChange={e => setForm(f => ({ ...f, fare_nzd: e.target.value }))} placeholder="e.g. 2500.00"
                className="bg-navy border-[rgba(255,255,255,0.1)] text-white h-10" required />
            </div>
            <div className="md:col-span-2">
              <Label className="text-text-secondary text-xs mb-1 block">Source Note</Label>
              <Input data-testid="override-source-input" value={form.source_note} onChange={e => setForm(f => ({ ...f, source_note: e.target.value }))} placeholder="e.g. Confirmed from CX direct sales"
                className="bg-navy border-[rgba(255,255,255,0.1)] text-white h-10" />
            </div>
            <div>
              <Label className="text-text-secondary text-xs mb-1 block">Valid Until</Label>
              <Input data-testid="override-valid-input" type="date" value={form.valid_until} onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))}
                className="bg-navy border-[rgba(255,255,255,0.1)] text-white h-10" />
            </div>
            <div className="flex items-end gap-2 md:col-span-3">
              <Button type="submit" data-testid="submit-override-btn" className="bg-ci-orange hover:bg-ci-orange/90 text-white h-10 px-6">Save Override</Button>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)} className="text-text-secondary hover:text-white h-10">Cancel</Button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Override History */}
      <div className="bg-card-bg border border-[rgba(255,255,255,0.08)] rounded-xl">
        <div className="p-4 border-b border-[rgba(255,255,255,0.08)]">
          <h3 className="font-syne font-bold text-white">Override History</h3>
        </div>
        {overrides.length === 0 ? (
          <div className="p-8 text-center text-text-secondary text-sm">No overrides yet. Click "Add Override" to get started.</div>
        ) : (
          <div className="divide-y divide-[rgba(255,255,255,0.06)]">
            {overrides.map((ov, i) => (
              <div key={ov.id || i} className="p-4 flex items-center justify-between gap-4" data-testid={`override-item-${i}`}>
                <div className="flex items-center gap-3">
                  <PushPin size={18} className="text-warning shrink-0" />
                  <div>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-white font-mono font-medium">{routeMap[ov.route_id] || ov.route_id}</span>
                      <span className="text-text-secondary">&middot;</span>
                      <span className="text-ci-blue font-medium">{ov.airline_iata}</span>
                      <span className="text-text-secondary">&middot;</span>
                      <span className="text-text-secondary capitalize">{ov.cabin_class?.replace('_', ' ')}</span>
                      <span className="text-text-secondary">&middot;</span>
                      <span className="text-text-secondary">{ov.travel_month}/{ov.travel_year}</span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs">
                      <span className="font-mono text-white font-bold">NZ${ov.fare_nzd?.toLocaleString()}</span>
                      {ov.source_note && <span className="text-text-secondary">{ov.source_note}</span>}
                      {ov.entered_by_name && <span className="text-text-secondary">by {ov.entered_by_name}</span>}
                    </div>
                  </div>
                </div>
                {canEdit && (
                  <button onClick={() => handleDelete(ov.id)} data-testid={`delete-override-${i}`}
                    className="text-text-secondary hover:text-danger transition-colors p-2">
                    <Trash size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
